import { useState, useEffect } from 'react';
import { Terminal, Monitor, Apple } from 'lucide-react';
import { DEFAULT_TZ, TZ_OPTIONS, timeAgo, liveHeartbeat, WsWarnBadge, useIsMobile } from '../../utils';
import { DelegatedButton } from './DelegatedButton';
import { DelegationPanel } from './DelegationPanel';
import { api } from '../../api';

function OsIcon({ os }) {
  const props = { size: 14, style: { verticalAlign: 'middle', marginRight: 4, flexShrink: 0 } };
  if (os === 'linux')   return <Terminal {...props} />;
  if (os === 'windows') return <Monitor {...props} />;
  if (os === 'macos')   return <Apple {...props} />;
  return <Terminal {...props} />;
}

function compareVersions(a, b) {
  // Retorna -1 si a < b, 0 si igual, 1 si a > b
  const pa = (a || '0').split('.').map(Number);
  const pb = (b || '0').split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i]||0) < (pb[i]||0)) return -1;
    if ((pa[i]||0) > (pb[i]||0)) return 1;
  }
  return 0;
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${(n / 1e3).toFixed(0)} KB`;
}

function HardDeleteServerModal({ server, onClose, onSuccess }) {
  const [hostnameInput, setHostnameInput] = useState('');
  const [deleteWasabi, setDeleteWasabi]   = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [wasabiSize, setWasabiSize]       = useState(null);
  const [sizeLoading, setSizeLoading]     = useState(true);

  useEffect(() => {
    api.getServerWasabiSize(server.id)
      .then(data => setWasabiSize(data))
      .catch(() => setWasabiSize({ size_bytes: 0, object_count: 0 }))
      .finally(() => setSizeLoading(false));
  }, [server.id]);

  const canConfirm = hostnameInput === server.hostname;

  const submit = async () => {
    setLoading(true); setError('');
    try {
      await api.hardDeleteServer(server.id, hostnameInput, deleteWasabi);
      onSuccess();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
  const boxStyle     = { background: 'var(--surface)', border: '1px solid var(--border)', width: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' };

  const wasabiLabel = sizeLoading
    ? 'consultando...'
    : wasabiSize && wasabiSize.size_bytes > 0
      ? `${formatBytes(wasabiSize.size_bytes)}, ${wasabiSize.object_count} objeto(s)`
      : 'sin datos en Wasabi';

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1, color: 'var(--red)' }}>ELIMINAR DEFINITIVAMENTE</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16, color: 'var(--red)' }}>
            ⚠ Esta operación es IRREVERSIBLE. Borra el registro del servidor, todos sus jobs, runs y dependencias de la base de datos.
          </div>
          <div style={{ fontSize: 13, marginBottom: 16 }}>
            Servidor: <strong>{server.hostname}</strong>
            {server.ip_address && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>{server.ip_address}</span>}
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer', padding: '12px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <input type="checkbox" checked={deleteWasabi} onChange={e => setDeleteWasabi(e.target.checked)} style={{ marginTop: 2, cursor: 'pointer', accentColor: 'var(--red)' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Borrar datos en Wasabi ahora
                <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>({wasabiLabel})</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--mono)' }}>⚠ Los snapshots se borran permanentemente y no se pueden recuperar.</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Si no lo marcás, los datos igual se borran en 90 días (mínimo de facturación de Wasabi). No marcarlo pospone el borrado, no lo evita.
              </div>
            </div>
          </label>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <div>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
              Escribí el hostname exacto para confirmar
            </label>
            <input
              type="text"
              value={hostnameInput}
              onChange={e => setHostnameInput(e.target.value)}
              placeholder={server.hostname}
              autoFocus
              style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${canConfirm ? 'var(--red)' : 'var(--border)'}`, color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={!canConfirm || loading} style={{ background: canConfirm ? 'var(--red)' : 'transparent', border: '1px solid var(--red)', color: canConfirm ? 'white' : 'var(--red)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontWeight: 700 }}>
            {loading ? 'ELIMINANDO...' : 'ELIMINAR DEFINITIVAMENTE'}
          </button>
        </div>
      </div>
    </div>
  );
}


function RegenerateTokenModal({ server, onClose, onRegenerated, setTab }) {
  const isMobile = useIsMobile();
  const [hostnameInput, setHostnameInput] = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [online, setOnline]             = useState(null);
  const [checkingOnline, setCheckingOnline] = useState(true);
  const [token, setToken]               = useState(null);
  const [copied, setCopied]             = useState('');
  const [applied, setApplied]           = useState(false);
  const [installToken, setInstallToken]     = useState(null);
  const [installTokenError, setInstallTokenError] = useState('');
  const [installTokenLoading, setInstallTokenLoading] = useState(false);

  useEffect(() => {
    api.getServerOnlineStatus(server.id)
      .then(data => setOnline(!!data.online))
      .catch(() => setOnline(null))
      .finally(() => setCheckingOnline(false));
  }, [server.id]);

  // Recién una vez regenerado el server_token generamos tambien un install
  // token: hace falta para el comando de "instalación desde cero" (#141) y no
  // tiene sentido gastar uno si el admin termina cancelando en el paso 1.
  useEffect(() => {
    if (!token) return;
    setInstallTokenLoading(true);
    api.getToken(server.client_id)
      .then(data => setInstallToken(data))
      .catch(e => setInstallTokenError(e.message))
      .finally(() => setInstallTokenLoading(false));
  }, [token, server.client_id]);

  const canConfirm = hostnameInput === server.hostname;

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const data = await api.regenerateServerToken(server.id, hostnameInput);
      setToken(data.server_token);
      onRegenerated && onRegenerated();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const copy = (label, text) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
  const boxStyle     = { background: 'var(--surface)', border: '1px solid var(--border)', width: isMobile ? '95vw' : 620, maxHeight: isMobile ? '90vh' : '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' };
  const monoBlock    = { background: 'var(--bg)', border: '1px solid var(--border)', padding: 12, fontFamily: 'var(--mono)', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 8 };
  const copyBtn      = () => ({ background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '5px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1, marginBottom: 16 });
  const sectionTitle = { fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' };
  const platformLabel = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' };

  // ---- Paso 2: token generado — se muestra UNA sola vez -------------------
  if (token) {
    // Vía A — el agente ya está instalado: editar config.env y reiniciar.
    const winEditCmd =
      `(Get-Content C:\\OpenBackup\\config.env) -replace '^OPENBACKUP_SERVER_TOKEN=.*', 'OPENBACKUP_SERVER_TOKEN=${token}' | Set-Content C:\\OpenBackup\\config.env\n` +
      `Stop-ScheduledTask -TaskName "OpenBackupAgent"\n` +
      `Start-ScheduledTask -TaskName "OpenBackupAgent"`;
    const linuxEditCmd =
      `sudo sed -i "s|^OPENBACKUP_SERVER_TOKEN=.*|OPENBACKUP_SERVER_TOKEN=${token}|" /etc/openbackup/config.env\n` +
      `sudo systemctl restart openbackup-agent`;

    // Vía B — nada instalado (el rollback del instalador borró todo, #141):
    // instalar de cero pasando el token nuevo, sin que el agente tenga que
    // esperar a que /register decida no rotarlo.
    const winFreshCmd = installToken
      ? `$env:OPENBACKUP_SERVER_TOKEN = "${token}"\n` +
        `iwr "${installToken.download_url_windows}" -UseBasicParsing | iex`
      : '';
    const linuxFreshCmd = installToken
      ? `export OPENBACKUP_SERVER_TOKEN=${token}\n` +
        `curl -fsSL "${installToken.download_url_linux}" | sudo -E bash`
      : '';

    return (
      <div style={overlayStyle}>
        <div style={boxStyle}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>TOKEN REGENERADO — {server.hostname}</span>
          </div>
          <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
            <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 20 }}>
              Esta es la ÚNICA vez que vas a ver este token. No se guarda en claro en ningún lado —
              si lo perdés hay que regenerarlo de nuevo.
            </div>

            <label style={platformLabel}>Token nuevo</label>
            <div style={{ ...monoBlock, color: 'var(--accent)', fontSize: 13 }}>{token}</div>
            <button onClick={() => copy('token', token)} style={copyBtn()}>{copied === 'token' ? '✓ COPIADO' : 'COPIAR TOKEN'}</button>

            <div style={sectionTitle}>Vía A — el agente ya está instalado</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
              Editá el config.env existente con el token nuevo y reiniciá el servicio.
            </div>

            <label style={platformLabel}>Windows (PowerShell, como Administrador)</label>
            <div style={monoBlock}>{winEditCmd}</div>
            <button onClick={() => copy('win-edit', winEditCmd)} style={copyBtn()}>{copied === 'win-edit' ? '✓ COPIADO' : 'COPIAR COMANDO WINDOWS'}</button>

            <label style={platformLabel}>Linux</label>
            <div style={monoBlock}>{linuxEditCmd}</div>
            <button onClick={() => copy('linux-edit', linuxEditCmd)} style={copyBtn()}>{copied === 'linux-edit' ? '✓ COPIADO' : 'COPIAR COMANDO LINUX'}</button>

            <div style={sectionTitle}>Vía B — instalación desde cero</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
              Usar cuando la máquina no tiene nada instalado (p.ej. el rollback del instalador
              borró todo). El comando de abajo instala el agente y le pasa el token nuevo, así
              no depende de que <code>/register</code> encuentre un token viejo que ya no existe.
            </div>

            {installTokenLoading && (
              <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16 }}>Generando install token del cliente...</div>
            )}
            {installTokenError && (
              <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>
                No se pudo generar el install token ({installTokenError}). Generalo manualmente
                desde {setTab ? (
                  <button onClick={() => { onClose(); setTab('token'); }} style={{ background: 'none', border: 'none', color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12, padding: 0 }}>
                    la pestaña "Token de Instalación"
                  </button>
                ) : 'la pestaña "Token de Instalación"'} y reemplazá <code>OPENBACKUP_SERVER_TOKEN</code> a mano en los comandos de esa pestaña.
              </div>
            )}
            {installToken && (
              <>
                <label style={platformLabel}>Windows (PowerShell, como Administrador)</label>
                <div style={monoBlock}>{winFreshCmd}</div>
                <button onClick={() => copy('win-fresh', winFreshCmd)} style={copyBtn()}>{copied === 'win-fresh' ? '✓ COPIADO' : 'COPIAR COMANDO WINDOWS'}</button>

                <label style={platformLabel}>Linux</label>
                <div style={monoBlock}>{linuxFreshCmd}</div>
                <button onClick={() => copy('linux-fresh', linuxFreshCmd)} style={copyBtn()}>{copied === 'linux-fresh' ? '✓ COPIADO' : 'COPIAR COMANDO LINUX'}</button>

                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 20 }}>
                  Ese install token vence: {new Date(installToken.expires_at).toLocaleString('es-AR')}
                </div>
              </>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 20, cursor: 'pointer' }}>
              <input type="checkbox" checked={applied} onChange={e => setApplied(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text)' }}>Copié el token / apliqué el comando</span>
            </label>

            <button
              onClick={onClose}
              disabled={!applied}
              style={{
                width: '100%', background: applied ? 'var(--accent)' : 'var(--accent2)',
                color: 'var(--bg)', border: 'none', padding: 14, fontFamily: 'var(--mono)',
                fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                cursor: applied ? 'pointer' : 'not-allowed', opacity: applied ? 1 : 0.5,
              }}
            >
              ENTENDIDO, CERRAR
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Paso 1: confirmación -----------------------------------------------
  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1, color: 'var(--red)' }}>REGENERAR TOKEN</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16, color: 'var(--red)' }}>
            ⚠ Esta acción desconecta INMEDIATAMENTE al agente que esté corriendo con el token
            actual. El agente no se recupera solo: hay que reinstalarlo o editar su config.env a
            mano con el token nuevo. Si apretás esto sobre el servidor equivocado, ese cliente se
            queda sin backups hasta que alguien vaya a la máquina.
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            Servidor: <strong>{server.hostname}</strong>
            {server.ip_address && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>{server.ip_address}</span>}
          </div>
          <div style={{
            fontSize: 12, fontFamily: 'var(--mono)', padding: '8px 12px', marginBottom: 16,
            background: 'var(--bg)', border: `1px solid ${online ? 'var(--red)' : 'var(--border)'}`,
            color: checkingOnline ? 'var(--muted)' : (online ? 'var(--red)' : 'var(--muted)'),
          }}>
            {checkingOnline ? 'Verificando estado del agente...'
              : online === null ? 'No se pudo verificar si el agente está conectado.'
              : online ? '● Agente CONECTADO ahora mismo — regenerar el token de un agente online es casi siempre un error.'
              : '○ Agente no conectado actualmente.'}
          </div>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <div>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
              Escribí el hostname exacto para confirmar
            </label>
            <input
              type="text"
              value={hostnameInput}
              onChange={e => setHostnameInput(e.target.value)}
              placeholder={server.hostname}
              autoFocus
              style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${canConfirm ? 'var(--red)' : 'var(--border)'}`, color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={!canConfirm || loading} style={{ background: canConfirm ? 'var(--red)' : 'transparent', border: '1px solid var(--red)', color: canConfirm ? 'white' : 'var(--red)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontWeight: 700 }}>
            {loading ? 'REGENERANDO...' : 'REGENERAR TOKEN'}
          </button>
        </div>
      </div>
    </div>
  );
}


export function ServersTab({ servers, heartbeatFeed, delegations, isClient, isAdmin, isOperator, isReseller, isOwnCompany, resellerPerms, operatorPerms, overrideActive, client, id, handleViewRepoKey, repoKeyLoadingId, setTransferModal, setTransferTarget, setConfirmDeleteServer, setAddServer, noInfraText, setTab, onGenerateToken, onReload, showInactive, setShowInactive }) {
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [regenerateTarget, setRegenerateTarget] = useState(null);
  const [toast, setToast] = useState({ msg: '', type: '' });
  // Lo mismo que exige el backend (require_permission('can_manage_servers') + pertenencia)
  const canManageServers = isAdmin
    || (isOperator && !!operatorPerms?.can_manage_servers)
    || (isReseller && !!resellerPerms?.can_manage_servers);
  const canGenerateToken = isAdmin || isOperator || isClient || (isReseller && !!resellerPerms?.can_generate_tokens);
  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  return (
    <div>
      {toast.msg && (
        <div style={{
          marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12,
          background: toast.type === 'ok' ? 'rgba(0,229,160,0.1)' : 'rgba(255,77,109,0.1)',
          border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : 'var(--red)'}`,
          color: toast.type === 'ok' ? 'var(--green)' : 'var(--red)',
        }}>
          {toast.type === 'ok' ? '✓' : '✕'} {toast.msg}
        </div>
      )}
      {hardDeleteTarget && (
        <HardDeleteServerModal
          server={hardDeleteTarget}
          onClose={() => setHardDeleteTarget(null)}
          onSuccess={() => {
            const name = hardDeleteTarget.hostname;
            setHardDeleteTarget(null);
            showToast(`Servidor ${name} eliminado definitivamente`);
            onReload && onReload();
          }}
        />
      )}
      {regenerateTarget && (
        <RegenerateTokenModal
          server={regenerateTarget}
          setTab={setTab}
          onClose={() => setRegenerateTarget(null)}
          onRegenerated={() => {
            showToast(`Token de ${regenerateTarget.hostname} regenerado`);
            onReload && onReload();
          }}
        />
      )}
      {/* #208, #210: el reseller delega la infraestructura de su empresa como lo haría el cliente, directo al admin */}
      {((isClient && !client.internal) || (isOwnCompany && resellerPerms?.can_manage_account)) && <DelegationPanel clientId={id} toAdmin={!isClient} />}
      {(isAdmin || isOperator) && setShowInactive && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none', fontFamily: 'var(--mono)' }}>
          <input
            type="checkbox"
            checked={showInactive || false}
            onChange={e => setShowInactive(e.target.checked)}
            style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
          />
          Mostrar también servidores desactivados
        </label>
      )}
      {servers.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          {noInfraText || 'Sin servidores. Instalá el agente para registrar el primer servidor.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          {servers.map((s,i) => {
            const hb = liveHeartbeat(s, heartbeatFeed);
            const osLabel = s.os_version || { linux: 'Linux', windows: 'Windows', macos: 'macOS' }[s.os_type] || s.os_type || '—';
            return (
            <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: s.active ? 1 : 0.6 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: hb.color, flexShrink: 0 }} title={hb.label} />
                  {hb.wsWarning && <WsWarnBadge />}
                  <OsIcon os={s.os_type} />
                  <span>{s.hostname}</span>
                  {!s.active && (
                    <span style={{ padding: '1px 6px', background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid #9ca3af', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      INACTIVO
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  {s.ip_address || '—'} · {osLabel}
                  {s.server_type && s.server_type !== 'other' && (
                    <span style={{ marginLeft: 6, padding: '1px 5px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {s.server_type}
                    </span>
                  )}
                  {s.timezone && s.timezone !== DEFAULT_TZ && (
                    <span style={{ marginLeft: 8, color: 'var(--accent)' }}>
                      · {TZ_OPTIONS.find(t => t.value === s.timezone)?.label || s.timezone}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: hb.color, marginTop: 2 }}>
                  {s.last_seen ? 'Último heartbeat: ' + timeAgo(s.last_seen) : 'Sin contacto'}
                  {s.agent_version && (
                    <span style={{ marginLeft: 8, color: (s.latest_in_channel && compareVersions(s.agent_version, s.latest_in_channel) < 0) ? 'var(--yellow)' : 'var(--muted)' }}>
                      · agente v{s.agent_version}{(s.latest_in_channel && compareVersions(s.agent_version, s.latest_in_channel) < 0) ? ` ⚠ actualizar a ${s.latest_in_channel}` : ''}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {s.active ? (
                  <>
                    {(isAdmin || isOperator || isReseller || isClient) && (!isOwnCompany || resellerPerms?.can_manage_account) && (
                      <DelegatedButton
                        operation="can_view_key"
                        delegations={delegations}
                        isClient={isClient}
                        overrideActive={overrideActive}
                        disabled={repoKeyLoadingId === s.id}
                        onClick={() => handleViewRepoKey(s)}
                        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '4px 10px', fontSize: 11, fontFamily: 'var(--mono)' }}
                      >
                        {repoKeyLoadingId === s.id ? '...' : 'CLAVE'}
                      </DelegatedButton>
                    )}
                    {isAdmin && (
                      <button onClick={() => { setTransferTarget(''); setTransferModal(s); }} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>TRANSFERIR</button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setRegenerateTarget(s)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>REGENERAR TOKEN</button>
                    )}
                    {canManageServers && (
                      <button onClick={() => setConfirmDeleteServer(s)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>ELIMINAR</button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setHardDeleteTarget(s)} style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 700 }}>ELIM. DEF.</button>
                    )}
                  </>
                ) : (
                  isAdmin && (
                    <button onClick={() => setHardDeleteTarget(s)} style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 700 }}>ELIM. DEF.</button>
                  )
                )}
              </div>
            </div>
          );})}
        </div>
      )}
      {noInfraText && servers.length > 0 && (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{noInfraText}</div>
      )}
      {!noInfraText && (canGenerateToken || canManageServers) && (
        <div style={{ display: 'flex', gap: 8 }}>
          {canManageServers && (
            <button onClick={() => setAddServer(true)} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>+ AGREGAR SERVIDOR</button>
          )}
          {canGenerateToken && (
            <button onClick={() => { setTab('token'); onGenerateToken && onGenerateToken(); }} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>GENERAR TOKEN</button>
          )}
        </div>
      )}
    </div>
  );
}
